/*
  SocialInbox — Facebook Messenger + Instagram DMs captured from Meta
  webhooks, surfaced as chat-style threads. Phase 1 is READ-ONLY:
  outbound replies happen in Meta Business Suite (we expose a deep
  link). First DM of every conversation auto-promotes to an Enquiry.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Facebook, Instagram, Inbox, Loader2, ExternalLink,
  MessageCircle, CheckCircle2, Clock, Search, RefreshCcw,
  ChevronLeft, Archive, ArchiveRestore, UserCircle2,
} from "lucide-react";

function PlatformIcon({ platform, className = "h-3.5 w-3.5" }) {
  if (platform === "instagram")
    return <Instagram className={`${className} text-pink-600`} />;
  return <Facebook className={`${className} text-blue-600`} />;
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - d.getTime()) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d`;
  return d.toLocaleDateString();
}

function formatMessageTime(timestampMs) {
  if (!timestampMs) return "";
  const d = new Date(timestampMs);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function ConversationListItem({ conv, active, onClick }) {
  const isUnread = (conv.unread_count || 0) > 0;
  const displayName = conv.participant_name
    || `${conv.platform === "instagram" ? "@" : ""}${(conv.participant_id || "").slice(-8)}`;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors ${
        active
          ? "border-indigo-600 bg-indigo-50/60"
          : "border-transparent hover:bg-zinc-50"
      }`}
      data-testid={`social-conv-item-${conv.id}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="relative flex-shrink-0 mt-0.5">
          <div className="h-9 w-9 rounded-full bg-zinc-200 flex items-center justify-center">
            <UserCircle2 className="h-6 w-6 text-zinc-500" />
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-[1px]">
            <PlatformIcon platform={conv.platform} className="h-3 w-3" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1.5">
            <div className={`text-xs ${isUnread ? "font-semibold text-zinc-950" : "font-medium text-zinc-800"} truncate`}>
              {displayName}
            </div>
            <div className="text-2xs text-zinc-500 flex-shrink-0">
              {relativeTime(conv.last_message_at)}
            </div>
          </div>
          <div className={`text-2xs mt-0.5 truncate ${isUnread ? "text-zinc-900" : "text-zinc-500"}`}>
            {conv.last_message_direction === "out" && <span className="text-zinc-400">You: </span>}
            {conv.last_message_preview || <span className="italic">(no preview)</span>}
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            {conv.page_name && (
              <span className="text-2xs text-zinc-400 truncate max-w-[120px]" title={conv.page_name}>
                {conv.page_name}
              </span>
            )}
            {conv.status === "closed" && (
              <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200 text-2xs px-1 py-0 h-4">closed</Badge>
            )}
            {conv.enquiry_id && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-2xs px-1 py-0 h-4">
                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />enquiry
              </Badge>
            )}
            {isUnread && (
              <span
                className="ml-auto inline-flex items-center justify-center min-w-[18px] h-4 rounded-full px-1 text-2xs font-semibold bg-indigo-600 text-white"
                data-testid={`social-conv-unread-${conv.id}`}
              >
                {conv.unread_count}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function MessageBubble({ msg }) {
  const isOut = msg.direction === "out";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} px-3`}>
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 text-xs shadow-sm ${
          isOut
            ? "bg-indigo-600 text-white rounded-br-sm"
            : "bg-white border border-zinc-200 text-zinc-800 rounded-bl-sm"
        }`}
        data-testid={`social-msg-${msg.id}`}
      >
        {msg.text && <div className="whitespace-pre-wrap break-words">{msg.text}</div>}
        {(msg.attachments || []).length > 0 && (
          <div className="mt-1 space-y-1">
            {msg.attachments.map((a, i) => (
              <div key={i} className={`text-2xs ${isOut ? "text-indigo-100" : "text-zinc-500"}`}>
                📎 {a.type || "attachment"}
                {a.url && (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`ml-1 underline ${isOut ? "text-white" : "text-indigo-700"}`}
                  >
                    view
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
        <div className={`text-2xs mt-1 ${isOut ? "text-indigo-200" : "text-zinc-400"}`}>
          {formatMessageTime(msg.timestamp_ms)}
        </div>
      </div>
    </div>
  );
}

export default function SocialInbox() {
  const [conversations, setConversations] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [activeId, setActiveId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [filter, setFilter] = useState("all"); // all | unread | facebook | instagram | closed
  const [search, setSearch] = useState("");
  const [backfilling, setBackfilling] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = { limit: 200 };
      if (filter === "unread") params.unread_only = true;
      else if (filter === "facebook") params.platform = "facebook";
      else if (filter === "instagram") params.platform = "instagram";
      else if (filter === "closed") params.status = "closed";
      else params.status = "open";
      const r = await api.get("/social/conversations", { params });
      setConversations(r.data.conversations || []);
      setUnreadTotal(r.data.unread_total || 0);
    } catch (e) {
      toast.error("Couldn't load conversations", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoadingList(false);
    }
  }, [filter]);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    setLoadingThread(true);
    try {
      const r = await api.get(`/social/conversations/${id}/messages`);
      setMessages(r.data.messages || []);
      setActiveConv(r.data.conversation);
      // Auto mark-read on open.
      if ((r.data.conversation?.unread_count || 0) > 0) {
        await api.post(`/social/conversations/${id}/read`);
        // Reflect locally without a full list refetch.
        setConversations((prev) =>
          prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c))
        );
        setUnreadTotal((n) => Math.max(0, n - (r.data.conversation?.unread_count || 0)));
      }
    } catch (e) {
      toast.error("Couldn't load thread", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (activeId) loadThread(activeId); }, [activeId, loadThread]);

  // Light auto-refresh so new DMs appear without a manual reload.
  useEffect(() => {
    const t = setInterval(() => { loadList(); }, 30000);
    return () => clearInterval(t);
  }, [loadList]);

  async function toggleStatus() {
    if (!activeConv) return;
    const next = activeConv.status === "closed" ? "open" : "closed";
    try {
      const r = await api.patch(`/social/conversations/${activeConv.id}`, { status: next });
      setActiveConv(r.data);
      toast.success(next === "closed" ? "Conversation closed" : "Conversation reopened");
      loadList();
    } catch (e) {
      toast.error("Couldn't update status", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function backfill() {
    setBackfilling(true);
    try {
      const r = await api.post("/social/ingest-pending?limit=200");
      toast.success(`Backfill complete — processed ${r.data.processed} events`, {
        description: `${r.data.created_enquiries} new enquiries, ${r.data.deduped} already present.`,
      });
      loadList();
      if (activeId) loadThread(activeId);
    } catch (e) {
      toast.error("Backfill failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBackfilling(false);
    }
  }

  const filteredConversations = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter((c) =>
      (c.participant_name || "").toLowerCase().includes(q)
      || (c.last_message_preview || "").toLowerCase().includes(q)
      || (c.page_name || "").toLowerCase().includes(q)
    );
  }, [conversations, search]);

  return (
    <div className="max-w-[1400px] mx-auto p-4 space-y-3" data-testid="social-inbox-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
            <Link to="/enquiries" className="text-zinc-400 hover:text-zinc-700" data-testid="social-back-link">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <MessageCircle className="h-6 w-6" /> Social Inbox
            {unreadTotal > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[22px] h-5 rounded-full px-1.5 text-2xs font-semibold bg-indigo-600 text-white"
                data-testid="social-unread-total"
              >
                {unreadTotal}
              </span>
            )}
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Facebook Messenger + Instagram DMs. First DM per thread auto-creates an enquiry. Replies happen in Meta Business Suite.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={backfill}
            disabled={backfilling}
            className="text-xs"
            data-testid="social-backfill-btn"
            title="Walk any Messenger / IG webhook events we stored before the Social Inbox ingest was wired, so historical DMs light up without a fresh delivery."
          >
            {backfilling
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <ArchiveRestore className="h-3.5 w-3.5 mr-1" />}
            Backfill from webhook log
          </Button>
          <Button
            variant="outline"
            onClick={() => { loadList(); if (activeId) loadThread(activeId); }}
            className="text-xs"
            data-testid="social-refresh-btn"
          >
            <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Two-pane layout */}
      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] min-h-[640px]">
          {/* Left pane — list */}
          <div className="border-r border-zinc-200 flex flex-col">
            {/* Filter + search */}
            <div className="p-2 border-b border-zinc-200 space-y-2">
              <div className="flex items-center gap-1 flex-wrap" data-testid="social-filter-tabs">
                {[
                  { k: "all", label: "All" },
                  { k: "unread", label: "Unread" },
                  { k: "facebook", label: "FB" },
                  { k: "instagram", label: "IG" },
                  { k: "closed", label: "Closed" },
                ].map((t) => (
                  <button
                    key={t.k}
                    onClick={() => setFilter(t.k)}
                    className={`text-2xs px-2 py-1 rounded-md ${
                      filter === t.k
                        ? "bg-zinc-900 text-white"
                        : "text-zinc-600 hover:bg-zinc-100"
                    }`}
                    data-testid={`social-filter-${t.k}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, text or page"
                  className="h-7 text-xs pl-6"
                  data-testid="social-search"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto" data-testid="social-conv-list">
              {loadingList && (
                <div className="p-4 text-xs text-zinc-500 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                </div>
              )}
              {!loadingList && filteredConversations.length === 0 && (
                <div className="p-6 text-center text-xs text-zinc-500" data-testid="social-conv-empty">
                  <Inbox className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
                  No conversations yet. New Messenger / Instagram DMs appear here within seconds.
                </div>
              )}
              {filteredConversations.map((c) => (
                <ConversationListItem
                  key={c.id}
                  conv={c}
                  active={c.id === activeId}
                  onClick={() => setActiveId(c.id)}
                />
              ))}
            </div>
          </div>

          {/* Right pane — thread */}
          <div className="flex flex-col bg-zinc-50">
            {!activeConv && (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center text-xs text-zinc-500 max-w-[260px]">
                  <MessageCircle className="h-7 w-7 mx-auto mb-2 text-zinc-300" />
                  Select a conversation on the left to see the full thread.
                </div>
              </div>
            )}
            {activeConv && (
              <>
                {/* Thread header */}
                <div className="bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="relative flex-shrink-0">
                      <div className="h-9 w-9 rounded-full bg-zinc-200 flex items-center justify-center">
                        <UserCircle2 className="h-6 w-6 text-zinc-500" />
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-[1px]">
                        <PlatformIcon platform={activeConv.platform} className="h-3 w-3" />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-zinc-950 truncate" data-testid="social-thread-name">
                        {activeConv.participant_name
                          || `${activeConv.platform === "instagram" ? "@" : ""}${(activeConv.participant_id || "").slice(-8)}`}
                      </div>
                      <div className="text-2xs text-zinc-500 truncate">
                        {activeConv.page_name} · {activeConv.platform}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {activeConv.enquiry_id && (
                      <Link to={`/enquiries?focus=${activeConv.enquiry_id}`}>
                        <Button variant="outline" size="sm" className="text-2xs h-7" data-testid="social-open-enquiry-btn">
                          <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" />
                          Open enquiry
                        </Button>
                      </Link>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-2xs h-7"
                      onClick={toggleStatus}
                      data-testid="social-toggle-status-btn"
                    >
                      {activeConv.status === "closed"
                        ? <><ArchiveRestore className="h-3 w-3 mr-1" />Reopen</>
                        : <><Archive className="h-3 w-3 mr-1" />Close</>}
                    </Button>
                    {activeConv.business_suite_url && (
                      <a href={activeConv.business_suite_url} target="_blank" rel="noreferrer">
                        <Button
                          size="sm"
                          className="text-2xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
                          data-testid="social-open-suite-btn"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Reply in Meta Business Suite
                        </Button>
                      </a>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto py-3 space-y-2" data-testid="social-messages">
                  {loadingThread && (
                    <div className="px-3 text-xs text-zinc-500 flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading messages…
                    </div>
                  )}
                  {!loadingThread && messages.length === 0 && (
                    <div className="px-3 text-xs text-zinc-500">No messages in this thread yet.</div>
                  )}
                  {messages.map((m) => (
                    <MessageBubble key={m.id} msg={m} />
                  ))}
                </div>

                {/* Phase-1 footer notice */}
                <div className="bg-white border-t border-zinc-200 px-4 py-2.5 text-2xs text-zinc-500 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  Phase 1 is read-only — click <b className="text-zinc-700 mx-0.5">Reply in Meta Business Suite</b> to respond.
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
